import type {CustomEventName} from "vite/types/customEvent.js";
import {GetOrganizationWideViewFeatures, NavigationEnum} from "../../../Enums";
import {useAppStore} from "../../../StateManager";
import type {Space} from "../../../data/models/Space";
import type {View} from "../../../data/models/View";
import type {IReportResult, Report} from "../../../data/models/Report";
import {XyiconFeature} from "../../../generated/api/base";
import {notify} from "../../../utils/Notify";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {NotificationType} from "../../notification/Notification";
import type {AppState} from "../../../data/state/AppState";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import type {AppActions} from "../../../data/state/AppActions";
import type {Portfolio} from "../../../data/models/Portfolio";

export const EventNameForViewsChangeInLocalStorage: CustomEventName = "viewsModifiedInLocalStorage";
export const getLocalStorageKeyForWorkspaceViews = (userId: string, orgId: string) => `workspace_viewtabs_${userId}_${orgId}`;
const getLocalStorageKeyForActiveWorkspaceView = (userId: string, orgId: string) => `active_workspace_viewtabs_${userId}_${orgId}`;

export type TCustomEventForViewsChangeInLocalStorage = {
	tabId: string;
};

export interface IViewTab {
	viewId: string;
	lastUsed: number; // Date.now()
	requestId?: string;
	isLoading?: boolean;
	portfolioId?: string;
	lastRunTime?: number;
	unvisited?: boolean;
}

// Represents a viewtab that redirects to a DetailsView, eg.: /p3/details/xyicon/x33
export interface IDetailsView {
	viewId: string; // x33, b21
	lastUsed: number; // Date.now();
}

const isViewOrReportOrganizationWide = (viewOrReport: View | Report): boolean => {
	return viewOrReport.ownFeature === XyiconFeature.View && GetOrganizationWideViewFeatures().includes((viewOrReport as View).itemFeature);
};

export const getWorkspaceViewTabsFromLocalStorage = (userId: string, orgId: string, actions: AppActions): IViewTab[] => {
	const localStorageKey = getLocalStorageKeyForWorkspaceViews(userId, orgId);
	const localStorageValue = localStorage.getItem(localStorageKey);
	const viewIdsInLocalStorage: IViewTab[] = localStorageValue ? JSON.parse(localStorageValue) : [];

	const filteredViewIdsInLocalStorage = viewIdsInLocalStorage.filter((viewTab: IViewTab) => {
		if (StringUtils.xyiconOrBoundaryRefIdPattern.test(viewTab.viewId)) {
			return true;
		}

		const viewOrReportMaybe = actions.getViewOrReportViewById(viewTab.viewId);
		if (!viewOrReportMaybe) {
			return false;
		}

		if (isViewOrReportOrganizationWide(viewOrReportMaybe)) {
			return false;
		}

		return true;
	});

	return filteredViewIdsInLocalStorage;
};

export const getActiveWorkspaceViewTabFromLocalStorage = (userId: string, orgId: string): IViewTab | null => {
	const localStorageKey = getLocalStorageKeyForActiveWorkspaceView(userId, orgId);
	const localStorageValue = localStorage.getItem(localStorageKey);
	return localStorageValue ? JSON.parse(localStorageValue) : null;
};

export const removeAllWorkspaceViewTabsExceptSelected = (appState: AppState) => {
	const {user, organizationIdByAuthData} = appState;

	if (user?.id) {
		const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user.id, organizationIdByAuthData);
		const activeViewTab = getActiveWorkspaceViewTabFromLocalStorage(user.id, organizationIdByAuthData);

		localStorage.setItem(localStorageKeyForWorkspaceViews, JSON.stringify([activeViewTab]));

		const view = appState.actions.getReportViewById(activeViewTab.viewId) || appState.actions.getViewById(activeViewTab.viewId);

		if (view) {
			dispatchEvent(new CustomEvent(EventNameForViewsChangeInLocalStorage, {detail: {noUpdate: true, tabViewId: view.id}}));
		}
	}
};

export const removeWorkspaceViewFromTabs = (viewId: string, appState: AppState, isInWorkspaceView: boolean = true) => {
	const userId = appState.user?.id;
	const orgId = appState.organizationIdByAuthData;
	const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(userId, orgId, appState.actions);
	const activeViewTab = getActiveWorkspaceViewTabFromLocalStorage(userId, orgId);

	const indexOfViewToBeDeleted = viewTabsInLocalStorage.findIndex((v) => v.viewId === viewId);

	if (indexOfViewToBeDeleted !== -1) {
		viewTabsInLocalStorage.splice(indexOfViewToBeDeleted, 1);

		const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(userId, orgId);
		const localStorageKeyForActiveWorkspaceViews = getLocalStorageKeyForActiveWorkspaceView(userId, orgId);

		localStorage.setItem(localStorageKeyForWorkspaceViews, JSON.stringify(viewTabsInLocalStorage));

		if (activeViewTab.viewId === viewId) {
			if (viewTabsInLocalStorage.length != 0) {
				let viewTabMostRecentlyUsed = viewTabsInLocalStorage[0];

				for (let i = 1; i < viewTabsInLocalStorage.length; ++i) {
					if (viewTabMostRecentlyUsed.lastUsed < viewTabsInLocalStorage[i].lastUsed) {
						viewTabMostRecentlyUsed = viewTabsInLocalStorage[i];
					}
				}

				const viewOrReportMaybe = appState.actions.getViewOrReportViewById(viewTabMostRecentlyUsed.viewId);

				if (viewOrReportMaybe) {
					if (isInWorkspaceView) {
						onWorkspaceViewClick(viewOrReportMaybe);
					} else {
						onWorkspaceViewSelect(viewOrReportMaybe);
					}
				} else if (StringUtils.xyiconOrBoundaryRefIdPattern.test(viewTabMostRecentlyUsed.viewId)) {
					selectDetailsViewTab(viewTabMostRecentlyUsed.viewId, appState);
				}
			}
			if (viewTabsInLocalStorage.length === 0) {
				localStorage.removeItem(localStorageKeyForWorkspaceViews);
				localStorage.removeItem(localStorageKeyForActiveWorkspaceViews);

				const viewMaybe = appState.actions.getViewOrReportViewById(viewId);
				if (viewMaybe) {
					appState.selectedViewId[viewMaybe.itemFeature] = "";
				}
			}
		}

		if (activeViewTab.viewId !== viewId || viewTabsInLocalStorage.length === 0) {
			// This conditional event creating is needed when the removed view is not the active view,
			// or there is no view tab left in the localstorage.
			window.dispatchEvent(new CustomEvent(EventNameForViewsChangeInLocalStorage, {detail: {noUpdate: true}}));
		}
	}
};

const saveWorkspaceTabs = (viewKey: string, activeKey: string, viewTabs: IViewTab[], activeViewTab: IViewTab) => {
	localStorage.setItem(viewKey, JSON.stringify(viewTabs));
	localStorage.setItem(activeKey, JSON.stringify(activeViewTab));
};

const showReportNotification = (report: Report) => {
	const appState = report.appState;
	const notification = notify(appState.app.notificationContainer, {
		type: NotificationType.Message,
		title: "",
		lifeTime: 10000,
	});

	if (report.errorMessage) {
		notify(appState.app.notificationContainer, {
			type: NotificationType.Error,
			title: report.errorMessage,
			lifeTime: Infinity,
		});
	} else {
		notification?.onClose();
		notify(appState.app.notificationContainer, {
			lifeTime: 10000,
			type: NotificationType.Message,
			title: `Report Complete - ${report.name}`,
			description: "Report generation is complete",
			buttonLabel: "Open View",
			onActionButtonClick: () => onWorkspaceViewClick(report, false),
		});
	}
};

const waitForReportGenerated = (appState: AppState, requestId: string) => {
	const reportGeneratedSignal = appState.app.transport.signalR.listener.signals.reportGenerated;

	return new Promise<IReportResult>((resolve, reject) => {
		const handler = (payload: IReportResult) => {
			if (payload?.requestID === requestId) {
				reportGeneratedSignal.remove(handler);
				resolve(payload);
			}
		};

		reportGeneratedSignal.add(handler);
	});
};

export const runAndHandleReport = async (report: Report, isPortfolioSwitched: boolean) => {
	const appState = report.appState;
	const {user, organizationIdByAuthData, app} = appState;

	if (!appState.reportsUnderGenerating.includes(report.id)) {
		appState.reportsUnderGenerating = [...appState.reportsUnderGenerating, report.id];

		await app.transport.services.feature.refreshList(XyiconFeature.Report);
		const result = await app.transport.services.feature.runSavedReport(report);
		const reportResult = await waitForReportGenerated(appState, result.requestID);

		if (reportResult.isSuccess) {
			const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
			const localStorageKeyForActiveWorkspaceView = getLocalStorageKeyForActiveWorkspaceView(user?.id, organizationIdByAuthData);
			let activeTab = getActiveWorkspaceViewTabFromLocalStorage(user?.id, organizationIdByAuthData);
			const timestamp = Date.now();

			appState.reportsUnderGenerating = appState.reportsUnderGenerating.filter((id) => id !== report.id);

			const newViewTab: IViewTab = {
				viewId: report.id,
				lastUsed: timestamp,
				requestId: result.requestID,
				isLoading: false,
				portfolioId: appState.activePortfolioId,
				lastRunTime: timestamp,
				unvisited: activeTab.viewId !== report.id,
			};

			const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(user?.id, organizationIdByAuthData, appState.actions);
			const updatedViewTabs = viewTabsInLocalStorage.map((vt) => (newViewTab.viewId === vt.viewId ? newViewTab : vt));

			// If this report is displayed on the active view tab,
			// then the newly created IViewTab object needs to be saved as the active tab storage item's value.
			if (activeTab.viewId === report.id) {
				activeTab = newViewTab;
			}

			saveWorkspaceTabs(localStorageKeyForWorkspaceViews, localStorageKeyForActiveWorkspaceView, updatedViewTabs, activeTab);

			window.dispatchEvent(new CustomEvent(EventNameForViewsChangeInLocalStorage, {detail: {tabViewId: report.id}}));

			if (report.id !== activeTab.viewId) {
				showReportNotification(report);
			}
		} else {
			report.completeRequest(reportResult, "");
			showReportNotification(report);
		}
	}
};

const saveRecentViewsToPreferences = (viewID: string, portfolioID: string, lastUsedAt: string, appState: AppState) => {
	appState.user.preferences.recentlyUsedViews = [{viewID, portfolioID, lastUsedAt}, ...(appState.user.preferences.recentlyUsedViews ?? [])];
};

const hasPortfolioSwitched = (viewOrReport: View | Report, portfolio: Portfolio) => {
	const appState = viewOrReport.appState;
	const {user, organizationIdByAuthData, actions} = appState;

	if (portfolio?.id !== appState.activePortfolioId) {
		return true;
	}

	const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(user?.id, organizationIdByAuthData, actions);
	const itemIndexInListMaybe = viewTabsInLocalStorage.findIndex((v) => v.viewId === viewOrReport.id);
	const currentTab = viewTabsInLocalStorage[itemIndexInListMaybe];
	const hasSwitched = currentTab?.portfolioId !== undefined && currentTab?.portfolioId !== appState.activePortfolioId;

	return hasSwitched;
};

export const onWorkspaceViewSelect = (viewOrReport: View | Report, forcePutInBeginningOfList: boolean = false, forceRun: boolean = false) => {
	const appState = viewOrReport.appState;
	const isReport = viewOrReport.ownFeature === XyiconFeature.Report;
	const {user, organizationIdByAuthData, actions} = appState;

	if (isViewOrReportOrganizationWide(viewOrReport)) {
		console.log(`A non-workspace view has been attempted to be open in a workspace! Get Peter to fix this!`);
		return; // NOT a workspace, don't do anything
	}

	const localStorageKeyForActiveWorkspaceView = getLocalStorageKeyForActiveWorkspaceView(user?.id, organizationIdByAuthData);
	const activeTab: IViewTab = JSON.parse(localStorage.getItem(localStorageKeyForActiveWorkspaceView));

	if (!viewOrReport.id) {
		// Default/system views don't have ids (empty string), so do nothing.
		return;
	}

	actions.selectView(viewOrReport);

	const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(user?.id, organizationIdByAuthData, actions);
	const itemIndexInListMaybe = viewTabsInLocalStorage.findIndex((v) => v.viewId === viewOrReport.id);
	const currentTab = viewTabsInLocalStorage[itemIndexInListMaybe];
	const isPortfolioSwitched = currentTab?.portfolioId !== undefined && currentTab?.portfolioId !== appState.activePortfolioId;

	if (viewOrReport.id === activeTab?.viewId && !forcePutInBeginningOfList) {
		// Selected tab is the same as the active (portfolio changes), save to recent views (Home Dashboard needs it), but don't reload.
		saveRecentViewsToPreferences(viewOrReport.id, appState.activePortfolioId, Date.now().toString(), appState);
		return;
	}

	if (currentTab && isReport && isPortfolioSwitched) {
		const report = appState.actions.getReportViewById(currentTab.viewId);

		if (report) {
			report.clearReport();
		}
	}

	const requestId = isReport && itemIndexInListMaybe !== -1 ? currentTab.requestId : "";
	const isLoading = isReport && (itemIndexInListMaybe === -1 || requestId === "");

	const newViewTab: IViewTab = {
		viewId: viewOrReport.id,
		lastUsed: Date.now(),
		requestId,
		isLoading,
		portfolioId: appState.activePortfolioId,
		lastRunTime: currentTab?.lastRunTime || Date.now(),
		unvisited: false,
	};

	addViewTabToTabsAndSelect(newViewTab, appState, isPortfolioSwitched, forcePutInBeginningOfList);

	const loggedInTime = new Date(appState.app.transport.services.auth.authData.issuedAt).getTime();
	const isOutDated = newViewTab.lastRunTime < loggedInTime;

	if (isReport && (isOutDated || !currentTab || isPortfolioSwitched || requestId === "" || !!forceRun)) {
		runAndHandleReport(viewOrReport as Report, isPortfolioSwitched);
	}
};

export const selectWorkspaceViewTabForFeature = (appState: AppState, feature: XyiconFeature, forcePutInBeginningOfList: boolean = false) => {
	const selectedView = appState.actions.getSelectedView(feature);

	if (selectedView) {
		onWorkspaceViewSelect(selectedView, forcePutInBeginningOfList);
	}
};

export const isDetailsTabView = (viewId?: string): boolean => {
	return viewId?.startsWith("x") || viewId?.startsWith("b");
};

export const selectDetailsViewTab = (viewId: string, appState: AppState, portfolioRefId?: string) => {
	if (isDetailsTabView(viewId)) {
		const moduleName = viewId.startsWith("x") ? "xyicon" : "boundary";
		appState.app.navigation.goApp({
			mainNav: NavigationEnum.DETAILS,
			subNavs: [moduleName, viewId],
			portfolioRefId,
		});
	} else {
		console.warn(`You tried to open a details tab view with a wrong viewId: ${viewId}`);
	}
};

export const addDetailsViewToTabsIfNeededAndSelect = (detailsView: IDetailsView, appState: AppState, forcePutInBeginningOfList: boolean = false) => {
	return addViewTabToTabsAndSelect(detailsView, appState, false, forcePutInBeginningOfList);
};

const addViewTabToTabsAndSelect = (
	viewTab: IViewTab,
	appState: AppState,
	isPortfolioSwitched: boolean,
	forcePutInBeginningOfList: boolean = false,
) => {
	const {user, organizationIdByAuthData} = appState;
	const localStorageKeyForActiveWorkspaceView = getLocalStorageKeyForActiveWorkspaceView(user?.id, organizationIdByAuthData);
	const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
	const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(user?.id, organizationIdByAuthData, appState.actions);
	viewTab.lastUsed = Date.now();
	let updatedViewTabsInLocalStorage: IViewTab[] = [];
	const itemIndexInListMaybe = viewTabsInLocalStorage.findIndex((v) => v.viewId === viewTab.viewId);

	if (itemIndexInListMaybe !== -1) {
		if (forcePutInBeginningOfList) {
			updatedViewTabsInLocalStorage = [viewTab, ...viewTabsInLocalStorage.filter((vt) => vt.viewId !== viewTab.viewId)];
		} else {
			updatedViewTabsInLocalStorage = viewTabsInLocalStorage.map((vt) => (viewTab.viewId === vt.viewId ? viewTab : vt));
		}
	} else {
		updatedViewTabsInLocalStorage = [viewTab, ...viewTabsInLocalStorage];
	}

	const activeTab: IViewTab = JSON.parse(localStorage.getItem(localStorageKeyForActiveWorkspaceView));

	if (appState.user && (!ObjectUtils.compare(viewTabsInLocalStorage, updatedViewTabsInLocalStorage) || !ObjectUtils.compare(activeTab, viewTab))) {
		saveWorkspaceTabs(localStorageKeyForWorkspaceViews, localStorageKeyForActiveWorkspaceView, updatedViewTabsInLocalStorage, viewTab);
		window.dispatchEvent(new CustomEvent(EventNameForViewsChangeInLocalStorage, {detail: {isPortfolioSwitched, tabViewId: viewTab.viewId}}));

		saveRecentViewsToPreferences(viewTab.viewId, appState.activePortfolioId, Date.now().toString(), appState);
	}
};

export const onWorkspaceViewClick = async (
	viewOrReport: View | Report,
	forcePutInBeginningOfList: boolean = false,
	setViewForOpenSpaceSelector?: (value: View | Report) => void,
	options: {portfolioRefId?: string; spaceRefId?: string} = {},
) => {
	const appState = viewOrReport?.appState;
	const {portfolioRefId = appState.activePortfolioRefId ?? "", spaceRefId} = options;
	const {isScopeWideSearchOpen, setIsScopeWideSearchOpen} = useAppStore.getState();

	if (isScopeWideSearchOpen) {
		setIsScopeWideSearchOpen(false);
	}

	if (viewOrReport?.itemFeature === XyiconFeature.SpaceEditor) {
		if (spaceRefId) {
			appState.app.navigation.goApp({
				portfolioRefId,
				mainNav: NavigationEnum.SPACE,
				subNavs: [spaceRefId],
			});
			onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
			return;
		}

		if (appState.app.spaceViewRenderer.isMounted && portfolioRefId === appState.activePortfolioRefId) {
			onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
		} else {
			const userId = appState.user?.id;
			const orgId = appState.organizationIdByAuthData;
			const localStorageKeyForLastViewedSpace = appState.getLocalStorageKeyForLastViewedSpace(userId, orgId);
			const lastViewedSpaceId = localStorage.getItem(localStorageKeyForLastViewedSpace);
			const lastViewedSpace = lastViewedSpaceId
				? (appState.actions.getFeatureItemById(lastViewedSpaceId, XyiconFeature.Space) ??
					appState.actions.getFeatureItemByRefId<Space>(lastViewedSpaceId, XyiconFeature.Space))
				: null;
			const portfolioMaybe = appState.actions.getPortfolioByRefId(portfolioRefId);
			const isSpacesModule = appState.selectedFeature === XyiconFeature.Space;

			// In case of a portfolio change, we could end up with empty Spaces state if spaces are not loaded as a dependency (switching from a Report view). We want to fetch spaces again.
			const shouldFetchSpaces = (hasPortfolioSwitched(viewOrReport, portfolioMaybe) || !lastViewedSpace) && !isSpacesModule;
			if (shouldFetchSpaces) {
				await appState.app.transport.services.feature.refreshList(XyiconFeature.Space, true, portfolioMaybe.id);
			}

			const spaces = appState.actions
				.getList<Space>(XyiconFeature.Space)
				.toSorted((a: Space, b: Space) => StringUtils.sortIgnoreCase(a.name, b.name));

			if (spaces.length > 0) {
				if (spaces.length > 1 && setViewForOpenSpaceSelector) {
					setViewForOpenSpaceSelector(viewOrReport);
				} else {
					const lastViewedSpaceMaybe = spaces.find((s) => s.id === lastViewedSpaceId || s.refId === lastViewedSpaceId);
					if (lastViewedSpaceMaybe) {
						appState.app.navigation.goApp({
							portfolioRefId,
							mainNav: NavigationEnum.SPACE,
							subNavs: [lastViewedSpaceMaybe.refId],
						});
					} else {
						appState.app.navigation.goApp({
							portfolioRefId,
							mainNav: NavigationEnum.SPACE,
							subNavs: [spaces[0].refId],
						});
					}
					onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
				}
			} else {
				appState.app.navigation.goApp({
					portfolioRefId,
					mainNav: NavigationEnum.SPACES,
				});
				onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
			}
		}
	} else if (viewOrReport?.ownFeature === XyiconFeature.Report) {
		appState.app.navigation.goApp({portfolioRefId, mainNav: NavigationEnum.REPORT_VIEWS});
		onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
	} else if (viewOrReport?.itemFeature === XyiconFeature.Xyicon) {
		appState.app.navigation.goApp({portfolioRefId, mainNav: NavigationEnum.XYICONS});
		onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
	} else if (viewOrReport?.itemFeature === XyiconFeature.Boundary) {
		appState.app.navigation.goApp({portfolioRefId, mainNav: NavigationEnum.BOUNDARIES});
		onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
	} else if (viewOrReport?.itemFeature === XyiconFeature.Space) {
		appState.app.navigation.goApp({portfolioRefId, mainNav: NavigationEnum.SPACES});
		onWorkspaceViewSelect(viewOrReport, forcePutInBeginningOfList);
	}
};
